import * as React from 'react';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';

// Light/dark theme. The pre-paint class is set by the inline script in
// public/index.html (anti-FOUC); this provider only syncs React state to the
// already-applied class and persists explicit user choices.
//
// Behaviour: with NO stored choice the app follows the OS (`prefers-color-scheme`)
// and live-updates when the system flips. Once the user toggles, that choice is
// persisted to localStorage and the system is ignored.

type Theme = 'light' | 'dark';

interface ThemeContextValue {
  theme: Theme;
  isDark: boolean;
  toggle: () => void;
  setTheme: (t: Theme) => void;
}

const STORAGE_KEY = 'chatly-theme';

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

const hasStoredPreference = (): boolean => {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return v === 'dark' || v === 'light';
  } catch {
    return false;
  }
};

const getInitialTheme = (): Theme => {
  if (typeof document !== 'undefined' && document.documentElement.classList.contains('dark')) {
    return 'dark';
  }
  if (typeof window !== 'undefined') {
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      if (stored === 'dark' || stored === 'light') return stored;
    } catch {
      /* localStorage unavailable — fall through to system preference */
    }
    if (window.matchMedia?.('(prefers-color-scheme: dark)').matches) return 'dark';
  }
  return 'light';
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setThemeState] = useState<Theme>(getInitialTheme);

  const applyTheme = useCallback(
    (next: Theme, opts: { withTransition: boolean; persist: boolean }) => {
      const root = document.documentElement;
      if (opts.withTransition) {
        root.classList.add('theme-transition');
        window.setTimeout(() => root.classList.remove('theme-transition'), 300);
      }
      root.classList.toggle('dark', next === 'dark');
      if (opts.persist) {
        try {
          window.localStorage.setItem(STORAGE_KEY, next);
        } catch {
          /* ignore write failures (private mode, etc.) */
        }
      }
    },
    []
  );

  // Keep the class in sync with state without animating the first paint.
  useEffect(() => {
    applyTheme(theme, { withTransition: false, persist: false });
  }, [theme, applyTheme]);

  // Follow the OS while the user has made no explicit choice.
  useEffect(() => {
    if (hasStoredPreference()) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (e: MediaQueryListEvent) => setThemeState(e.matches ? 'dark' : 'light');
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const setTheme = useCallback(
    (next: Theme) => {
      applyTheme(next, { withTransition: true, persist: true });
      setThemeState(next);
    },
    [applyTheme]
  );

  const toggle = useCallback(() => {
    setThemeState((prev) => {
      const next: Theme = prev === 'dark' ? 'light' : 'dark';
      applyTheme(next, { withTransition: true, persist: true });
      return next;
    });
  }, [applyTheme]);

  return (
    <ThemeContext.Provider value={{ theme, isDark: theme === 'dark', toggle, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextValue => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within a ThemeProvider');
  return ctx;
};
